import type { Messages } from '@ah/i18n';

/** English translation (ADR-015). Any key missing here falls back to el.ts. */
export const en: Messages = {
  auth: {
    login: {
      title: 'Therapist sign in',
      email: 'Email',
      password: 'Password',
      submit: 'Sign in',
      submitting: 'Signing in…',
      missingFields: 'Please enter your email and password.',
      resendPrompt: "Can't find the confirmation email? We can send it again.",
      resendButton: 'Resend the email',
      resendSent: 'If an account exists for that address, a new link has been sent. Check your spam folder too.',
      resendFailed: 'Something went wrong. Try again shortly.',
      wrongPortal: 'This portal is for therapists only. Please sign in with a therapist account.',
      failed: 'Sign in failed. Please try again.',
      noAccount: 'New to Anonymous Help as a therapist?',
      registerLink: 'Register',
    },
    twoFactor: {
      prompt: 'Enter the six-digit code from your authenticator app or your email.',
      codeLabel: 'Code',
      submit: 'Confirm',
      failed: 'Incorrect code. Try again.',
      recoveryHint: "If you can't access it, you can enter a recovery code (e.g. ABCDE-12345).",
    },
    register: {
      title: 'Register as a therapist',
      intro: "Your account is reviewed before you can be matched with clients — you can sign in and explore the portal in the meantime.",
      email: 'Email',
      password: 'Password (min 10 chars, mixed case + number)',
      displayName: 'Display name',
      displayNamePlaceholder: 'e.g. Dr. Maria Papadopoulou',
      licenseNumber: 'License number',
      licenseCountry: 'License country (2-letter code)',
      professionalType: 'Professional type',
      typePsychologist: 'Licensed psychologist',
      typeCounselor: 'Counsellor / coach',
      professionalTypeHint: 'Our team confirms this before approval. The landing page lists psychologists and counsellors separately.',
      specialties: 'Specialties (comma-separated)',
      specialtiesPlaceholder: 'e.g. anxiety, trauma, relationships',
      specialtiesError: 'List at least one specialty, separated by commas.',
      failed: 'Registration failed. Please try again.',
      legalNoticePrefix: 'By registering you accept the',
      legalNoticeJoiner: 'and the',
      termsLink: 'Terms of Use',
      privacyLink: 'Privacy Policy',
      alreadyRegistered: 'Already registered?',
      loginLink: 'Sign in',
      submit: 'Create therapist account',
      submitting: 'Creating your account…',
    },
  },
  verifyEmail: {
    title: 'Confirm your email',
    success: 'Your email is confirmed. You can sign in now.',
    already: 'This email was already confirmed. You can sign in as usual.',
    noToken: 'The link is incomplete. Open it directly from the email.',
    failed: "We couldn't confirm this email.",
    failedHint: 'Request a new link from the sign-in page.',
    goToLogin: 'Go to sign in',
  },
  dashboard: {
    title: 'Therapist Portal',
    myProfile: 'My profile',
    myServices: 'Services & pricing',
    payouts: 'Payouts',
    settings: 'Settings',
    signOut: 'Sign out',
    pendingReview: "Your account is pending review — you can explore the portal now, but you won't be matched with clients until an admin approves it.",
    suspended: 'Your account is suspended. Contact an administrator for details.',
    rejected: 'Your application was not approved. Contact an administrator for details.',
    loading: 'Loading…',
    loadError: 'Could not load your bookings.',
    yourBookings: 'Your bookings',
    noBookings: 'No bookings yet.',
    working: 'Working…',
    accept: 'Accept',
    decline: 'Decline',
    declinePrompt: 'Reason for declining (optional):',
    acceptError: 'Could not accept this booking. Please try again.',
    declineError: 'Could not decline this booking. Please try again.',
    awaitingPayment: 'Awaiting client payment.',
    openCall: 'Open call',
  },
  payouts: {
    title: 'Payouts',
    backDashboard: '← Dashboard',
    connectStatusTitle: 'Stripe Connect status',
    onboarded: "✅ Payments are set up — you'll receive 90% of each booking, paid out weekly.",
    notFullySetUp: "Your Stripe account isn't fully set up yet — finish onboarding to start receiving payouts.",
    notConnected: "You haven't connected a payout account yet.",
    completeOnboarding: 'Complete Stripe onboarding',
    redirecting: 'Redirecting…',
    onboardingError: 'Could not start onboarding.',
    loadError: 'Could not load payment info.',
    payoutHistory: 'Payout history',
    noPayouts: 'No payouts yet.',
    bookingsSuffix: 'booking(s)',
    net: 'net',
  },
  settings: {
    title: 'Account settings',
    backDashboard: '← Dashboard',
    language: 'Language',
    deleteAccount: 'Delete account',
    scheduledFor: 'Your account is scheduled for deletion on {{date}}. You can cancel any time before then.',
    cancelling: 'Cancelling…',
    cancelRequest: 'Cancel deletion request',
    graceNotice: "This starts a {{days}}-day grace period during which you can still cancel. After that, your public profile, messages, and login are permanently anonymized. Please make sure you have no upcoming bookings and have contacted support about any pending payout first — booking and invoice records are kept as required by tax law, disconnected from your identity. Enter your password to confirm.",
    password: 'Password',
    submitting: 'Submitting…',
    confirmDeletion: 'Confirm deletion',
    cancel: 'Cancel',
    deleteMyAccount: 'Delete my account',
    deletionRequestError: 'Could not submit the deletion request.',
    deletionCancelError: 'Could not cancel the deletion request.',
  },
  profile: {
    title: 'My profile',
    backDashboard: '← Dashboard',
    intro: "This is what clients see on the landing page: photo, headline, bio, intro video, specializations, and the case types you take on.",
    loading: 'Loading…',
    loadError: 'Could not load your profile.',
    photo: 'Profile photo',
    photoUpdated: 'Photo updated.',
    photoUploadFailed: 'Upload failed.',
    tabEl: 'Greek',
    tabEn: 'English',
    requiredBadge: 'required',
    optionalBadge: 'optional',
    completeBadge: '✓ complete',
    incompleteBadge: 'incomplete',
    headlineLabel: 'Headline (e.g. "Clinical Psychologist, 12 years experience")',
    bioLabel: 'Bio / description',
    introVideoLabel: 'Intro video (YouTube/Vimeo link — optional, shared across both languages)',
    categoriesLegend: 'Categories (specializations — pick from the list, shared across both languages)',
    caseTagsLabel: 'Case types I take on (comma-separated tags)',
    caseTagsPlaceholderEl: 'e.g. panic attacks, grief, burnout',
    caseTagsPlaceholderEn: 'e.g. panic attacks, grief, burnout',
    spokenLanguagesLegend: 'Session languages (which languages you can hold the session in)',
    save: 'Save profile',
    saving: 'Saving…',
    saveSuccess: "Profile saved — it's live on the landing page immediately (once you're approved).",
    saveFailed: 'Save failed.',
  },
  call: {
    connecting: 'Connecting…',
    waitingForOther: 'Waiting for the client to join…',
    ending: 'Ending call…',
    ended: 'Call ended — {{duration}}',
    anonymousNotice: 'Anonymous call — your voice is disguised, camera is off.',
    mute: 'Mute',
    unmute: 'Unmute',
    endCall: 'End call',
    done: 'Done',
    back: 'Back',
    genericError: 'Something went wrong.',
    needSignIn: 'You need to sign in to join this call.',
    joinError: 'Could not join the call.',
  },
  services: {
    backDashboard: '← Dashboard',
    title: 'Services & pricing',
    intro: 'Set what you offer and what it costs. You can have as many services as you like — psychotherapy at 50€ and coaching at 80€, say. Clients see exactly what is active here.',
    noneActive: 'You have no active service, so nobody can book you — however complete your profile otherwise looks.',
    empty: "You haven't added any services yet.",
    priceLabel: 'Price',
    clientPays: 'The client pays {{amount}}',
    savePrice: 'Save price',
    hide: 'Hide',
    show: 'Show',
    remove: 'Delete',
    visible: 'Visible',
    hidden: 'Hidden',
    addTitle: 'Add a service',
    choosePlaceholder: 'Choose a service…',
    pricePlaceholder: 'e.g. 50',
    add: 'Add',
    allAdded: 'You have already added every available service.',
    saved: 'Saved.',
    loadError: 'Could not load your services.',
    saveError: 'Saving failed. Check the price and try again.',
    confirmRemove: 'Delete the service "{{name}}"? Completed sessions are unaffected.',
  },
  common: {
    loading: 'Loading…',
    cancel: 'Cancel',
  },
  twoFactor: {
    title: 'Two-step verification',
    intro: 'A second step at sign-in, so your password alone is not enough for someone to get into your account.',
    off: 'Currently off.',
    onTotp: 'On — via an authenticator app.',
    onEmail: 'On — with a code sent to your email.',
    turnOn: 'Turn on',
    turnOff: 'Turn off',
    totpTitle: 'Authenticator app',
    totpBody: 'Stronger. You need an app such as Google Authenticator on your phone.',
    chooseTotp: 'Use an app',
    emailTitle: 'Code by email',
    emailBody: 'Simpler. We send a six-digit code each time you sign in. No app needed.',
    chooseEmail: 'Use email',
    scanHint: 'Scan the QR code with your app, then type the six-digit code it shows.',
    qrAlt: 'QR code for your authenticator app',
    manualKey: 'Or enter the key manually',
    emailSent: 'We sent a six-digit code to {{email}}.',
    resend: 'Send again',
    resent: 'A new code was sent to {{email}}.',
    codeLabel: 'Code',
    confirm: 'Confirm',
    recoveryTitle: 'Recovery codes',
    recoveryBody: 'Keep these somewhere safe. They are shown only now and we cannot show them again. Without them, losing your phone or your mailbox means losing the account.',
    savedThem: "I've saved them",
    disableHint: 'For safety this needs both your password and a current two-step code.',
    passwordLabel: 'Password',
    disabled: 'Two-step verification is off.',
    genericError: 'Something went wrong. Try again.',
  },
};
